/**
 * BillingSuccess Page
 * Shown after successful Stripe checkout
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { billingAPI } from '../../services/api';

function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollSubscriptionStatus();
    } else {
      setStatus('success');
    }
  }, [searchParams]);

  const pollSubscriptionStatus = async () => {
    // Poll for subscription status to confirm it's active
    let attempts = 0;
    const maxAttempts = 10;

    const checkStatus = async () => {
      try {
        const response = await billingAPI.getStatus();
        if (response.data?.subscription?.status === 'active') {
          setStatus('success');
          return;
        }
      } catch (err) {
        console.error('Error checking status:', err);
      }

      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkStatus, 2000);
      } else {
        // Assume success after timeout
        setStatus('success');
      }
    };

    checkStatus();
  };

  const handleContinue = () => {
    navigate('/owner');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {status === 'loading' ? (
          <>
            <div className="spinner w-12 h-12 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-navy-900 mb-2">
              Confirming your subscription...
            </h1>
            <p className="text-navy-600">
              Please wait while we activate your account.
            </p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>

            <h1 className="text-2xl font-bold text-navy-900 mb-2">
              Welcome to 1crew3!
            </h1>
            <p className="text-navy-600 mb-6">
              Your subscription is now active. You have full access to all features.
            </p>

            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <p className="text-green-800 text-sm">
                Your account has been successfully upgraded. You can now create unlimited mooring requests and connect with helpers worldwide.
              </p>
            </div>

            <button
              onClick={handleContinue}
              className="w-full btn-primary inline-flex items-center justify-center gap-2"
            >
              Go to Dashboard
              <ArrowRight className="h-4 w-4" />
            </button>

            <p className="text-xs text-navy-500 mt-4">
              You can manage your subscription anytime from the Billing page.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default BillingSuccess;
